import { getLocalWishlist, getProductGidByHandle, updateRemoteWishlist, CONSTANTS } from "./novicell_wishlist-utils";

/**
 * Reflect the local wishlist count in the header icon: fill the heart and
 * show a "(n)" bubble next to it. Mirrors the cart icon-bubble pattern, but
 * driven from localStorage since the wishlist lives client-side.
 */
function syncWishlistHeader() {
  const container = document.querySelector('.header__utility--wishlist');
  if (!container) return;

  const count = getLocalWishlist().filter(Boolean).length;
  container.classList.toggle('is-active', count > 0);

  const bubble = container.querySelector('[data-wishlist-count-bubble]');
  if (!bubble) return;

  if (count > 0) {
    bubble.textContent = String(count);
    bubble.hidden = false;
  } else {
    bubble.textContent = '';
    bubble.hidden = true;
  }
}

document.addEventListener('wishlist:updated', syncWishlistHeader);
document.addEventListener('wishlist:removed', syncWishlistHeader);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', syncWishlistHeader);
} else {
  syncWishlistHeader();
}

/**
 * Shared IntersectionObserver across every <wishlist-button> on the page.
 * On a 40-card collection grid this means one observer instance instead of 40,
 * and the canvas/Image work for any given card is deferred until it's about to
 * scroll into view — the cost scales with what the user actually sees, not
 * with the size of the catalog.
 */
const wishlistViewportObserver = typeof IntersectionObserver !== 'undefined'
  ? new IntersectionObserver(
    (entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target); // one-shot per card
        entry.target.runIconAdaptation();
      });
    },
    { rootMargin: '200px' }, // start sampling slightly before the card is on-screen
  )
  : null;

// requestIdleCallback is missing in Safari — fall back to a 0 ms timeout so the
// canvas read still happens off the critical thread.
const scheduleIdle = typeof requestIdleCallback === 'function'
  ? (cb) => requestIdleCallback(cb, { timeout: 1000 })
  : (cb) => setTimeout(cb, 0);

// WCAG relative luminance (0–1) from 8-bit sRGB channels, including the
// gamma-expansion step — so the light/dark icon decision matches perceived
// contrast rather than a naïve channel average.
function srgbToLinear(channel) {
  const c = channel / 255;
  return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

function relativeLuminance(r, g, b) {
  return 0.2126 * srgbToLinear(r) + 0.7152 * srgbToLinear(g) + 0.0722 * srgbToLinear(b);
}

if (!customElements.get('wishlist-button')) {
  class WishlistButton extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.updateWishlistState(false);
      this.initializeEventListeners();
      this.scheduleIconAdaptation();
    }

    disconnectedCallback() {
      // Stop observing if the element is removed before it ever entered the
      // viewport (e.g. infinite-scroll pagination replacing the grid).
      wishlistViewportObserver?.unobserve(this);
    }

    /**
     * Defer the icon-luminance work until the card is near the viewport,
     * then run it during browser idle time so a batch of cards entering
     * together doesn't stall scrolling.
     */
    scheduleIconAdaptation() {
      if (wishlistViewportObserver) {
        wishlistViewportObserver.observe(this);
      } else {
        // No IntersectionObserver — best we can do is defer to idle time.
        scheduleIdle(() => this.runIconAdaptation());
      }
    }

    runIconAdaptation() {
      scheduleIdle(() => this.adaptIconToImage());
    }

    /**
     * Sample the top-right corner of the closest product image and switch the
     * icon between white and dark depending on the area's brightness, so the
     * heart stays legible on both light and dark imagery.
     */
    adaptIconToImage() {
      const card = this.closest('.card-wrapper, .card-wrapper-new, .recent-item');
      const sourceImg = card?.querySelector('.card__media img, .media img, img');
      if (!sourceImg) return;

      // Product shots are typically transparent PNGs — the colour the shopper
      // actually sees behind the heart is the card's media background, not the
      // image pixels. Resolve it now so transparent corners are composited
      // against the real backdrop rather than read as black.
      const background = this.resolveBackdrop(sourceImg);

      const evaluate = () => {
        const src = sourceImg.currentSrc || sourceImg.src;
        if (!src) return;

        // Load via a separate Image() with CORS so the canvas isn't tainted.
        // Shopify's CDN serves `Access-Control-Allow-Origin: *` on image
        // assets, so this works without changing the markup.
        const probe = new Image();
        probe.crossOrigin = 'anonymous';
        probe.decoding = 'async';
        probe.onload = () => this.classifyByLuminance(probe, background);
        probe.onerror = () => { /* leave default (white) icon */ };
        probe.src = src;
      };

      if (sourceImg.complete && sourceImg.naturalWidth > 0) {
        evaluate();
      } else {
        sourceImg.addEventListener('load', evaluate, { once: true });
      }
    }

    /**
     * Walk up from the image to the first ancestor with a non-transparent
     * background colour — that's what shows through transparent product PNGs.
     * Falls back to white when nothing opaque is found.
     */
    resolveBackdrop(el) {
      let node = el;
      while (node && node.nodeType === 1) {
        const match = getComputedStyle(node).backgroundColor.match(/rgba?\(([^)]+)\)/);
        if (match) {
          const parts = match[1].split(',').map((p) => parseFloat(p));
          const alpha = parts.length === 4 ? parts[3] : 1;
          if (alpha > 0) return { r: parts[0], g: parts[1], b: parts[2] };
        }
        node = node.parentElement;
      }
      return { r: 255, g: 255, b: 255 };
    }

    classifyByLuminance(img, background = { r: 255, g: 255, b: 255 }) {
      try {
        const sample = 32;
        const canvas = document.createElement('canvas');
        canvas.width = sample;
        canvas.height = sample;
        const ctx = canvas.getContext('2d', { willReadFrequently: false });
        // Sample the top-right corner of the image — that's where the heart sits.
        const sx = Math.max(0, img.naturalWidth - sample);
        const sy = 0;
        ctx.drawImage(img, sx, sy, sample, sample, 0, 0, sample, sample);
        const { data } = ctx.getImageData(0, 0, sample, sample);

        let rTotal = 0;
        let gTotal = 0;
        let bTotal = 0;
        const pixels = data.length / 4;
        for (let i = 0; i < data.length; i += 4) {
          // Composite each pixel over the resolved backdrop so transparent
          // areas contribute the colour the shopper actually sees, not black.
          const alpha = data[i + 3] / 255;
          rTotal += data[i] * alpha + background.r * (1 - alpha);
          gTotal += data[i + 1] * alpha + background.g * (1 - alpha);
          bTotal += data[i + 2] * alpha + background.b * (1 - alpha);
        }

        const luminance = relativeLuminance(rTotal / pixels, gTotal / pixels, bTotal / pixels);
        // Pick whichever icon colour has the higher WCAG contrast against the
        // sampled area instead of a fixed brightness cut-off. The crossover
        // sits at ~0.179 relative luminance, so light-but-not-white backdrops
        // (beige, light grey) correctly get the dark heart.
        const contrastWhite = 1.05 / (luminance + 0.05);
        const contrastBlack = (luminance + 0.05) / 0.05;
        this.classList.toggle('wishlist-button--on-light', contrastBlack > contrastWhite);
      } catch (e) {
        // Canvas tainted or other error — keep the default white icon.
      }
    }

    get button() {
      return this.querySelector('button');
    }

    get productHandle() {
      return this.button?.dataset.productHandle;
    }

    get localWishlist() {
      return getLocalWishlist();
    }

    initializeEventListeners() {
      if (!this.button) return;

      this.button.disabled = false;
      this.button.addEventListener('click', this.handleWishlistToggle.bind(this));
    }

    handleWishlistToggle() {
      this.updateWishlistState();
    }
    
    saveWishlistToLocalStorage(wishlist) {
      const wishlistString = wishlist.join(CONSTANTS.LOCAL_STORAGE_DELIMITER);
      localStorage.setItem(CONSTANTS.LOCAL_STORAGE_WISHLIST_KEY, wishlistString);
    }

    async syncRemoteWishlist() {
      if (CONSTANTS.CUSTOMER_LOGGED_IN) {
        const productGid = await getProductGidByHandle(this.productHandle);
        if (productGid) await updateRemoteWishlist(productGid);
      }
      document.dispatchEvent(
        new CustomEvent('wishlist:updated', {
          detail: { wishlist: this.localWishlist },
        })
      );
    }

    async updateWishlistState(shouldChangeState = true) {
      const wishlist = this.localWishlist;
      const productIndex = wishlist?.indexOf(this.productHandle);
      let isActive = productIndex !== -1;

      if (shouldChangeState) {
        if (isActive) {
          wishlist.splice(productIndex, 1);
        } else {
          wishlist.push(this.productHandle);
        }
        
        isActive = !isActive; 
        this.saveWishlistToLocalStorage(wishlist);
        this.updateButtonAppearance(isActive);

        await this.syncRemoteWishlist();
      } else {
        this.updateButtonAppearance(isActive);
      }
    }

    updateButtonAppearance(isActive) {
      if (isActive) {
        this.classList.add(CONSTANTS.BUTTON_ACTIVE_CLASS);
        this.button.setAttribute('aria-checked', 'true');
      } else {
        this.classList.remove(CONSTANTS.BUTTON_ACTIVE_CLASS);
        this.button.setAttribute('aria-checked', 'false');
      }
    }
  }

  customElements.define('wishlist-button', WishlistButton);
}